import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { PromoCode, PromoCodeCreateRequest, PromoCodeDetail, PromoCodesResponse, PromoCodeUpdateRequest } from '@manaro/contracts'
import { queryKeys } from '@/shared/api/query-keys.ts'
import { createPromoCode, fetchPromoCode, fetchPromoCodes, updatePromoCode } from './api.ts'
import { replacePromo } from './form.ts'

export function usePromoCodes() {
  return useQuery({
    queryKey: queryKeys.promoCodes,
    queryFn: ({ signal }) => fetchPromoCodes(signal),
    staleTime: 60 * 1000,
  })
}

export function usePromoCode(id: number) {
  return useQuery({
    queryKey: queryKeys.promoCode(id),
    queryFn: ({ signal }) => fetchPromoCode(id, signal),
    enabled: Number.isInteger(id) && id > 0,
    staleTime: 60 * 1000,
  })
}

/** Puts the saved code into the list and the open card without another round trip. */
function useStorePromo() {
  const queryClient = useQueryClient()

  return (promo: PromoCode) => {
    queryClient.setQueryData<PromoCodesResponse>(queryKeys.promoCodes, (data) =>
      data ? { ...data, rows: replacePromo(data.rows, promo) } : data,
    )
    queryClient.setQueryData<PromoCodeDetail>(queryKeys.promoCode(promo.id), (data) => (data ? { ...data, ...promo } : data))
  }
}

export function useCreatePromoCode() {
  const queryClient = useQueryClient()
  const store = useStorePromo()

  return useMutation({
    mutationFn: (request: PromoCodeCreateRequest) => createPromoCode(request),
    onSuccess: store,
    // A lost answer may still have created the code: reload the list before anyone tries again.
    onError: () => queryClient.invalidateQueries({ queryKey: queryKeys.promoCodes }),
  })
}

export function useUpdatePromoCode() {
  const queryClient = useQueryClient()
  const store = useStorePromo()

  return useMutation({
    mutationFn: ({ id, request }: { id: number; request: PromoCodeUpdateRequest }) => updatePromoCode(id, request),
    onSuccess: store,
    onError: () => queryClient.invalidateQueries({ queryKey: queryKeys.promoCodes }),
  })
}
